import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI, meetAPI } from '../../services/api';
import './TeacherPages.css';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalStudents: 0,
    todayLessons: 0,
    weeklyLessons: 0,
    completedLessons: 0
  });
  const [todayBookings, setTodayBookings] = useState([]);
  const [recentStudents, setRecentStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [bookingsRes, studentsRes] = await Promise.all([
        meetAPI.getBookings({ upcoming: 'true' }),
        usersAPI.getStudents()
      ]);
      
      const bookings = bookingsRes.data.bookings;
      const students = studentsRes.data.students;
      
      // 今日のレッスン
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const todayLessons = bookings.filter(b => {
        const date = new Date(b.scheduled_at);
        return date >= today && date < tomorrow;
      });
      
      setTodayBookings(todayLessons);
      setRecentStudents(students.slice(0, 5));
      
      setStats({
        totalStudents: students.length,
        todayLessons: todayLessons.length,
        weeklyLessons: bookings.length,
        completedLessons: bookings.filter(b => b.status === 'completed').length
      });
      
    } catch (error) {
      console.error('Dashboard data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex-center" style={{ height: '50vh' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="teacher-dashboard fade-in">
      {/* ウェルカムセクション */}
      <section className="teacher-welcome">
        <div className="welcome-content">
          <h1>おはようございます、{user?.name}先生</h1>
          <p className="welcome-subtitle">
            Good morning! Ready to teach Japanese today?
          </p>
        </div>
        <Link to="/teacher/schedule" className="btn btn-primary">
          📅 View Full Schedule
        </Link>
      </section>

      {/* 統計カード */}
      <section className="teacher-stats">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <span className="stat-value">{stats.totalStudents}</span>
            <span className="stat-label">Total Students</span>
            <span className="stat-label-jp">生徒総数</span>
          </div>
        </div>
        <div className="stat-card highlight-today">
          <div className="stat-icon">📅</div>
          <div className="stat-info">
            <span className="stat-value">{stats.todayLessons}</span>
            <span className="stat-label">Today's Lessons</span>
            <span className="stat-label-jp">今日のレッスン</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📚</div>
          <div className="stat-info">
            <span className="stat-value">{stats.weeklyLessons}</span>
            <span className="stat-label">This Week</span>
            <span className="stat-label-jp">今週のレッスン</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <span className="stat-value">{stats.completedLessons}</span>
            <span className="stat-label">Completed</span>
            <span className="stat-label-jp">完了済み</span>
          </div>
        </div>
      </section>

      <div className="teacher-content-grid">
        {/* 今日のスケジュール */}
        <section className="teacher-section">
          <div className="section-header">
            <h2>📅 Today's Schedule / 今日の予定</h2>
            <Link to="/teacher/schedule" className="view-all">View All →</Link>
          </div>
          <div className="schedule-list">
            {todayBookings.length > 0 ? (
              todayBookings.map((booking) => (
                <div key={booking.id} className="schedule-item">
                  <div className="schedule-time">
                    {formatTime(booking.scheduled_at)}
                  </div>
                  <div className="schedule-info">
                    <div className="student-avatar-small">
                      {booking.student_picture ? (
                        <img src={booking.student_picture} alt="" />
                      ) : (
                        <span>{booking.student_name?.charAt(0)}</span>
                      )}
                    </div>
                    <div>
                      <h4>{booking.student_name}</h4>
                      <p>{booking.lesson_title || 'General Lesson'}</p>
                    </div>
                  </div>
                  <div className="schedule-actions">
                    {booking.google_meet_link && (
                      <a 
                        href={booking.google_meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                      >
                        Start Meet
                      </a>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-schedule">
                <p>No lessons scheduled for today</p>
                <p className="text-muted">今日の予定はありません</p>
              </div>
            )}
          </div>
        </section>

        {/* 最近の生徒 */}
        <section className="teacher-section">
          <div className="section-header">
            <h2>👥 Recent Students / 最近の生徒</h2>
            <Link to="/teacher/students" className="view-all">View All →</Link>
          </div>
          <div className="students-list">
            {recentStudents.map((student) => (
              <div key={student.id} className="student-item">
                <div className="student-avatar">
                  {student.profile_picture ? (
                    <img src={student.profile_picture} alt="" />
                  ) : (
                    <span>{student.name?.charAt(0)}</span>
                  )}
                </div>
                <div className="student-info">
                  <h4>{student.name}</h4>
                  <p>{student.email}</p>
                </div>
                <span className={`level-badge level-${student.japanese_level?.toLowerCase()}`}>
                  {student.japanese_level || 'N5'}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* クイックアクション */}
      <section className="teacher-actions">
        <h2>Quick Actions / クイックアクション</h2>
        <div className="actions-grid">
          <Link to="/teacher/schedule" className="action-card">
            <span className="action-icon">📹</span>
            <span className="action-label">Start Instant Meet</span>
            <span className="action-jp">即時ミーティング開始</span>
          </Link>
          <Link to="/teacher/students" className="action-card">
            <span className="action-icon">➕</span>
            <span className="action-label">Schedule New Lesson</span>
            <span className="action-jp">新しいレッスンを予約</span>
          </Link>
          <Link to="/teacher/lessons" className="action-card">
            <span className="action-icon">📝</span>
            <span className="action-label">Create Course</span>
            <span className="action-jp">コース作成</span>
          </Link>
          <Link to="/teacher/students" className="action-card">
            <span className="action-icon">📊</span>
            <span className="action-label">Student Progress</span>
            <span className="action-jp">生徒の進捗</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
